import {
  AGENT_BUILDER_LOAD_AGENTS_SUCCESS,
  AGENT_BUILDER_LOAD_AGENTS_FAILURE,
  AGENT_BUILDER_LOAD_TOOLS_SUCCESS,
  AGENT_BUILDER_LOAD_TOOLS_FAILURE,
  AGENT_BUILDER_CREATE_AGENT_SUCCESS,
  AGENT_BUILDER_CREATE_AGENT_FAILURE,
  AGENT_BUILDER_UPDATE_AGENT_SUCCESS,
  AGENT_BUILDER_UPDATE_AGENT_FAILURE,
  AGENT_BUILDER_DELETE_AGENT_SUCCESS,
  AGENT_BUILDER_DELETE_AGENT_FAILURE,
  AGENT_BUILDER_ASSOCIATE_TOOL_SUCCESS,
  AGENT_BUILDER_ASSOCIATE_TOOL_FAILURE,
  AGENT_BUILDER_REMOVE_TOOL_SUCCESS,
  AGENT_BUILDER_REMOVE_TOOL_FAILURE,
  AGENT_BUILDER_SET_LOADING,
  AGENT_BUILDER_SET_ERROR,
  // New action types
  AGENT_BUILDER_SET_CURRENT_AGENT,
  AGENT_BUILDER_SET_UNSAVED_CHANGES,
  AGENT_BUILDER_SET_NODES,
  AGENT_BUILDER_SET_CONNECTIONS,
  AGENT_BUILDER_SAVE_HIERARCHY_SUCCESS,
  AGENT_BUILDER_SAVE_HIERARCHY_FAILURE,
  AGENT_BUILDER_SET_HIERARCHY_TREE,
  AGENT_BUILDER_SIDEBAR_LAYOUT_LOADED,
  AGENT_BUILDER_SIDEBAR_LAYOUT_SET,
} from '../actions/async/agentBuilder';

const initialState = {
  // Data
  agents: [],
  tools: [],
  workflows: [],
  sidebarLayout: {
    agentFolders: [],
    toolFolders: [],
    toolAssignments: {},
  },
  nodes: [],
  connections: [],

  // Current agent state
  currentAgent: null,
  hasUnsavedChanges: false,
  selectedTools: [],


  currentWorkflow: null,
  workflowSettings: {},
  workflowMeta: {},

  // Hierarchy
  hierarchyTree: [],
  hierarchyMap: new Map(),

  // UI State
  loading: false,
  error: null,
  selectedNode: null,
  selectedConnection: null,

  // Modal States
  showCreateAgentModal: false,
  showEditAgentModal: false,
  showToolModal: false,

  // Form Data
  formData: {
    agent: {},
    tool: {},
  },
};

const agentBuilderReducer = (state = initialState, action) => {
  switch (action.type) {
    // Loading States
    case AGENT_BUILDER_SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case AGENT_BUILDER_SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    // Load Agents
    case AGENT_BUILDER_LOAD_AGENTS_SUCCESS:
      console.log('Reducer: AGENT_BUILDER_LOAD_AGENTS_SUCCESS with payload:', action.payload);
      return {
        ...state,
        agents: action.payload,
        error: null,
      };

    case AGENT_BUILDER_LOAD_AGENTS_FAILURE:
      return {
        ...state,
        error: action.payload,
      };

    // Load Tools
    case AGENT_BUILDER_LOAD_TOOLS_SUCCESS:
      console.log('Reducer: AGENT_BUILDER_LOAD_TOOLS_SUCCESS with payload:', action.payload);
      return {
        ...state,
        tools: action.payload,
        error: null,
      };

    case AGENT_BUILDER_SIDEBAR_LAYOUT_LOADED:
    case AGENT_BUILDER_SIDEBAR_LAYOUT_SET:
      return {
        ...state,
        sidebarLayout: {
          agentFolders: Array.isArray(action.payload?.agentFolders) ? action.payload.agentFolders : [],
          toolFolders: Array.isArray(action.payload?.toolFolders) ? action.payload.toolFolders : [],
          toolAssignments:
            action.payload?.toolAssignments && typeof action.payload.toolAssignments === 'object'
              ? { ...action.payload.toolAssignments }
              : {},
        },
      };

    case AGENT_BUILDER_LOAD_TOOLS_FAILURE:
      return {
        ...state,
        error: action.payload,
      };

    // Add Tool (optimistic update)
    case 'AGENT_BUILDER_ADD_TOOL':
      console.log('Reducer: AGENT_BUILDER_ADD_TOOL with payload:', action.payload);
      return {
        ...state,
        tools: [...state.tools, action.payload],
        error: null,
      };

    // Create Agent
    case AGENT_BUILDER_CREATE_AGENT_SUCCESS:
      return {
        ...state,
        agents: [...state.agents, action.payload],
        showCreateAgentModal: false,
        formData: { ...state.formData, agent: {} },
        error: null,
      };

    case AGENT_BUILDER_CREATE_AGENT_FAILURE:
      return {
        ...state,
        error: action.payload,
      };

    // Update Agent
    case AGENT_BUILDER_UPDATE_AGENT_SUCCESS:
      return {
        ...state,
        agents: state.agents.map(agent =>
          agent.id === action.payload.agentId
            ? action.payload.agent
            : agent
        ),
        showEditAgentModal: false,
        formData: { ...state.formData, agent: {} },
        error: null,
      };

    case AGENT_BUILDER_UPDATE_AGENT_FAILURE:
      return {
        ...state,
        error: action.payload,
      };

    // Delete Agent
    case AGENT_BUILDER_DELETE_AGENT_SUCCESS:
      return {
        ...state,
        agents: state.agents.filter(agent => agent.id !== action.payload),
        selectedNode: null,
        error: null,
      };

    case AGENT_BUILDER_DELETE_AGENT_FAILURE:
      return {
        ...state,
        error: action.payload,
      };

    // Associate Tool with Agent
    case AGENT_BUILDER_ASSOCIATE_TOOL_SUCCESS:
      return {
        ...state,
        agents: state.agents.map(agent =>
          agent.id === action.payload.agentId
            ? { ...agent, tools: [...(agent.tools || []), action.payload.toolName] }
            : agent
        ),
        error: null,
      };

    case AGENT_BUILDER_ASSOCIATE_TOOL_FAILURE:
      return {
        ...state,
        error: action.payload,
      };

    // Remove Tool from Agent
    case AGENT_BUILDER_REMOVE_TOOL_SUCCESS:
      return {
        ...state,
        agents: state.agents.map(agent =>
          agent.id === action.payload.agentId
            ? { ...agent, tools: (agent.tools || []).filter(tool => tool !== action.payload.toolName) }
            : agent
        ),
        error: null,
      };

    case AGENT_BUILDER_REMOVE_TOOL_FAILURE:
      return {
        ...state,
        error: action.payload,
      };

    // Update Agent Tools (used when loading tools for specific agent)
    case 'AGENT_BUILDER_UPDATE_AGENT_TOOLS':
      return {
        ...state,
        agents: state.agents.map(agent =>
          agent.id === action.payload.agentId
            ? { ...agent, tools: action.payload.tools }
            : agent
        ),
      };

    // Node Management
    case 'AGENT_BUILDER_ADD_NODE':
      return {
        ...state,
        nodes: [...state.nodes, action.payload],
      };

    case 'AGENT_BUILDER_UPDATE_NODE':
      return {
        ...state,
        nodes: state.nodes.map(node =>
          node.id === action.payload.id
            ? { ...node, ...action.payload }
            : node
        ),
      };

    case 'AGENT_BUILDER_DELETE_NODE':
      return {
        ...state,
        nodes: state.nodes.filter(node => node.id !== action.payload),
        connections: state.connections.filter(conn =>
          conn.source !== action.payload && conn.target !== action.payload
        ),
        selectedNode: state.selectedNode === action.payload ? null : state.selectedNode,
      };

    case 'AGENT_BUILDER_SELECT_NODE':
      return {
        ...state,
        selectedNode: action.payload,
        selectedConnection: null,
      };

    // Connection Management
    case 'AGENT_BUILDER_ADD_CONNECTION':
      return {
        ...state,
        connections: [...state.connections, action.payload],
      };

    case 'AGENT_BUILDER_DELETE_CONNECTION':
      return {
        ...state,
        connections: state.connections.filter(conn => conn.id !== action.payload),
        selectedConnection: state.selectedConnection === action.payload ? null : state.selectedConnection,
      };

    case 'AGENT_BUILDER_SELECT_CONNECTION':
      return {
        ...state,
        selectedConnection: action.payload,
        selectedNode: null,
      };

    // Modal Management
    case 'AGENT_BUILDER_SHOW_CREATE_AGENT_MODAL':
      return {
        ...state,
        showCreateAgentModal: true,
        formData: { ...state.formData, agent: {} },
      };

    case 'AGENT_BUILDER_HIDE_CREATE_AGENT_MODAL':
      return {
        ...state,
        showCreateAgentModal: false,
        formData: { ...state.formData, agent: {} },
      };

    case 'AGENT_BUILDER_SHOW_EDIT_AGENT_MODAL':
      return {
        ...state,
        showEditAgentModal: true,
        formData: { ...state.formData, agent: action.payload },
      };

    case 'AGENT_BUILDER_HIDE_EDIT_AGENT_MODAL':
      return {
        ...state,
        showEditAgentModal: false,
        formData: { ...state.formData, agent: {} },
      };

    case 'AGENT_BUILDER_SHOW_TOOL_MODAL':
      return {
        ...state,
        showToolModal: true,
        formData: { ...state.formData, tool: action.payload || {} },
      };

    case 'AGENT_BUILDER_HIDE_TOOL_MODAL':
      return {
        ...state,
        showToolModal: false,
        formData: { ...state.formData, tool: {} },
      };

    case 'AGENT_BUILDER_SHOW_MODEL_MODAL':
      return {
        ...state,
        showModelModal: true,
        formData: { ...state.formData, model: action.payload || {} },
      };

    case 'AGENT_BUILDER_HIDE_MODEL_MODAL':
      return {
        ...state,
        showModelModal: false,
        formData: { ...state.formData, model: {} },
      };

    case 'AGENT_BUILDER_SHOW_MEMORY_MODAL':
      return {
        ...state,
        showMemoryModal: true,
        formData: { ...state.formData, memory: action.payload || {} },
      };

    case 'AGENT_BUILDER_HIDE_MEMORY_MODAL':
      return {
        ...state,
        showMemoryModal: false,
        formData: { ...state.formData, memory: {} },
      };

    // Form Data Management
    case 'AGENT_BUILDER_UPDATE_FORM_DATA':
      return {
        ...state,
        formData: {
          ...state.formData,
          [action.payload.formType]: {
            ...state.formData[action.payload.formType],
            ...action.payload.data,
          },
        },
      };

    case 'AGENT_BUILDER_RESET_FORM_DATA':
      return {
        ...state,
        formData: {
          agent: {},
          tool: {},
          model: {},
          memory: {},
        },
      };

    // Canvas Management
    case 'AGENT_BUILDER_UPDATE_CANVAS':
      return {
        ...state,
        nodes: action.payload.nodes || state.nodes,
        connections: action.payload.connections || state.connections,
      };

    case 'AGENT_BUILDER_ZOOM_CANVAS':
      return {
        ...state,
        canvasZoom: action.payload,
      };

    case 'AGENT_BUILDER_PAN_CANVAS':
      return {
        ...state,
        canvasPan: action.payload,
      };

    // Undo/Redo
    case 'AGENT_BUILDER_UNDO':
      return {
        ...state,
        nodes: state.history?.undo?.nodes || state.nodes,
        connections: state.history?.undo?.connections || state.connections,
      };

    case 'AGENT_BUILDER_REDO':
      return {
        ...state,
        nodes: state.history?.redo?.nodes || state.nodes,
        connections: state.history?.redo?.connections || state.connections,
      };

    // Save History
    case 'AGENT_BUILDER_SAVE_HISTORY':
      return {
        ...state,
        history: {
          undo: {
            nodes: state.nodes,
            connections: state.connections,
          },
          redo: null,
        },
      };

    // New cases for enhanced save functionality
    case AGENT_BUILDER_SET_CURRENT_AGENT:
      return {
        ...state,
        currentAgent: action.payload,
      };

    case AGENT_BUILDER_SET_UNSAVED_CHANGES:
      return {
        ...state,
        hasUnsavedChanges: action.payload,
      };

    case AGENT_BUILDER_SET_NODES:
      console.log('AgentBuilder reducer - AGENT_BUILDER_SET_NODES received:', action.payload);
      return {
        ...state,
        nodes: action.payload,
        hasUnsavedChanges: true,
      };

    case AGENT_BUILDER_SET_CONNECTIONS:
      console.log('AgentBuilder reducer - AGENT_BUILDER_SET_CONNECTIONS received:', action.payload);
      return {
        ...state,
        connections: action.payload,
        hasUnsavedChanges: true,
      };

    case AGENT_BUILDER_SAVE_HIERARCHY_SUCCESS:
      return {
        ...state,
        error: null,
      };

    case AGENT_BUILDER_SAVE_HIERARCHY_FAILURE:
      return {
        ...state,
        error: action.payload,
      };

    case AGENT_BUILDER_SET_HIERARCHY_TREE:
      return {
        ...state,
        hierarchyTree: action.payload.tree,
        hierarchyMap: action.payload.map,
      };

    // New workflow action handlers
    case 'AGENT_BUILDER_SET_CURRENT_WORKFLOW':
      return {
        ...state,
        currentWorkflow: action.payload,
      };

    case 'AGENT_BUILDER_SET_WORKFLOW_SETTINGS':
      return {
        ...state,
        workflowSettings: action.payload,
        hasUnsavedChanges: true,
      };

    case 'AGENT_BUILDER_SET_WORKFLOW_META':
      return {
        ...state,
        workflowMeta: action.payload,
        hasUnsavedChanges: true,
      };

    case 'AGENT_BUILDER_SET_WORKFLOWS':
      return {
        ...state,
        workflows: action.payload,
      };

    case 'WORKFLOW_SAVING_START':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'WORKFLOW_SAVE_SUCCESS':
    case 'WORKFLOW_CREATE_SUCCESS':
      return {
        ...state,
        loading: false,
        error: null,
        currentWorkflow: action.payload,
        hasUnsavedChanges: false,
      };

    case 'WORKFLOW_SAVE_FAILURE':
    case 'WORKFLOW_CREATE_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    	case 'WORKFLOWS_LOAD_SUCCESS':
	  console.log('Reducer: WORKFLOWS_LOAD_SUCCESS with payload:', action.payload);
	  return {
		...state,
		workflows: action.payload,
		loading: false,
		error: null,
	  };

	case 'WORKFLOW_UPDATE_SUCCESS':
	  return {
		...state,
		workflows: state.workflows.map(workflow =>
		  workflow.workflowId === action.payload.workflowId
			? action.payload
			: workflow
		),
		loading: false,
		error: null,
	  };

	case 'WORKFLOW_UPDATE_FAILURE':
	  return {
		...state,
		loading: false,
		error: action.payload,
	  };

	case 'WORKFLOW_DELETE_SUCCESS':
	  return {
		...state,
		workflows: state.workflows.filter(workflow => workflow.workflowId !== action.payload),
		loading: false,
		error: null,
	  };

	case 'WORKFLOW_DELETE_FAILURE':
	  return {
		...state,
		loading: false,
		error: action.payload,
	  };

    case 'WORKFLOWS_LOAD_FAILURE':
      return {
        ...state,
        workflows: [],
        loading: false,
        error: action.payload,
      };

    default:
      return state;
  }
};

export default agentBuilderReducer;